import React from 'react';
import { useSelector } from 'react-redux';
import { Grid, GridItem, Heading, Image, Text } from '@chakra-ui/react';
import { OccupancyType, StudentSelectionMode, SystemTranslationKeys } from '@common/entities';
import { RootState } from 'state/store';

import { NoFloorPlanImage } from 'assets/images';
import { useSystemTranslation } from 'hooks';
import { useStudentSelectionMode } from 'hooks/floor-plans';
import { useLocale } from 'hooks/utils/useLocale';
import type { FloorPlan, Unit } from 'types/floorplan-page-types';
import { formatDateForLocale } from 'utils/format';

import SelectedLeaseTerms from './SelectedLeaseTerms';

export function SelectedUnitDetails() {
  const { tSystem } = useSystemTranslation();
  const locale = useLocale();
  const { floorPlan, unit, leaseTerm, moveInDate } = useSelector(
    (state: RootState) => state.unitSelection,
  );
  const { availableSpaceConfigurationIds } = useSelector(
    (state: RootState) => state.studentUnitSelection,
  );
  const { occupancyType } = useSelector((state: RootState) => state.application.form);
  const studentSelectionMode = useStudentSelectionMode();

  // Check if we're in student bulk assignment mode
  const isStudentBulkMode =
    occupancyType === OccupancyType.Student &&
    studentSelectionMode === StudentSelectionMode.BULK_UNIT_ASSIGNMENT;

  const { media, id: floorPlanId, name: floorPlanName, spaceOptions } = floorPlan as FloorPlan;

  const parseDateString = (dateString: string) => {
    const dateOnlyRegex =
      /^([0-9]([0-9]([0-9][1-9]|[1-9]0)|[1-9]00)|[1-9]000)(-(0[1-9]|1[0-2])(-(0[1-9]|[1-2][0-9]|3[0-1])))$/;
    if (dateOnlyRegex.test(dateString)) {
      const utcDate = new Date(dateString);
      const localDate = new Date(utcDate.getTime() + utcDate.getTimezoneOffset() * 60000);
      return localDate;
    }
    return new Date(dateString);
  };
  const date = parseDateString(moveInDate);
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const moveInDateString = formatDateForLocale(dateOnly, locale);

  // Determine heading based on mode
  let heading: string;
  let headingKey: string;

  if (isStudentBulkMode && !unit) {
    // Bulk mode: show floorPlan name + space option name
    const selectedSpaceOptionId = availableSpaceConfigurationIds[0];
    const selectedSpaceOption = spaceOptions?.find((so) => so.id === selectedSpaceOptionId);
    const spaceOptionName = selectedSpaceOption?.name || '';
    heading = spaceOptionName ? `${floorPlanName} - ${spaceOptionName}` : floorPlanName;
    headingKey = `bulk-heading-${floorPlanId}-${selectedSpaceOptionId}`;
  } else {
    // Regular mode: show unit number
    const { number, id: unitId } = unit as Unit;
    heading = number;
    headingKey = `unit-heading-${unitId}`;
  }

  return (
    <Grid
      templateColumns="repeat(3, 1fr)"
      gap={2}
      p={2}
      mt={3}
      border="1px solid"
      borderColor="silver.highlight"
      borderRadius="16px"
      boxShadow="0px 1px 3px rgba(0, 0, 0, 0.15)"
    >
      <GridItem colSpan={1} key={`unit-image-${floorPlanId}`} position="relative">
        <Image
          src={media[0]?.fullsizeUri || NoFloorPlanImage}
          alt={media[0]?.caption ?? 'floor-plan'}
          width="100%"
          height="100%"
          objectFit="contain"
          borderLeftRadius="12px"
          borderRightRadius="4px"
        />
      </GridItem>
      <GridItem colSpan={2} pl={1}>
        <Heading as="h5" size="sm" my={1} key={headingKey}>
          {heading}
        </Heading>
        <Text fontSize="xs" as="em">
          {tSystem(SystemTranslationKeys.cart.moveIn)}: {moveInDateString}
        </Text>
        {leaseTerm && (
          <SelectedLeaseTerms
            occupancyType={occupancyType as OccupancyType}
            leaseTerm={leaseTerm}
            isSUS={!isStudentBulkMode}
          />
        )}
      </GridItem>
    </Grid>
  );
}
